<template>
  <section class="notify-me" :class="{ desktop: $isDesktop }">
    <div v-if="submitted" class="thanks-msg">
      <img
        loading="lazy"
        class="tick-icon"
        src="@/assets/images/tick.svg"
        width="15"
        height="13"
        alt=""
      />
      <translate v-if="campsiteName" :tt="{ campsiteName }">
        Thanks for requesting an alert. We'll let you know when there's
        availability for your dates at {campsiteName}.
      </translate>
      <translate v-else>
        Thanks for requesting an alert. We'll let you know when more
        availability is released for your dates.
      </translate>
    </div>
    <div v-else>
      <allocation-alert-form
        v-if="singlePitchType"
        :pitchtypes="pitchtypes"
        :date-range="dateRange"
        :party="party"
        @submitted="hideForm"
      />
      <ui-collapse v-else :selected="true" :collapse-id="`collapse-notify-me`">
        <template #ui-collapse-icon-start>
          <div class="currency-icon">
            <img src="@/assets/images/bell.svg" loading="lazy" />
          </div>
        </template>
        <template #ui-collapse-title>
          <translate tag="div" class="expand-title">
            Leave your email address and we'll let you know when there's more
            availability for your dates.
          </translate>
        </template>
        <template #ui-collapse-icon>
          <div class="expand-chevron">
            <span class="ui-collapse-icon end"
              ><span class="chevron-l" /><span class="chevron-r"
            /></span>
          </div>
        </template>
        <template #ui-collapse-body>
          <allocation-alert-form
            v-if="isSearchResultAlert"
            :is-search-result-alert="true"
            :search-filters="searchFilters"
            :date-range="dateRange"
            :party="party"
            @submitted="hideForm"
          />
          <allocation-alert-form
            v-else
            :pitchtypes="pitchtypes"
            :date-range="dateRange"
            :party="party"
            @submitted="hideForm"
          />
        </template>
      </ui-collapse>
    </div>
  </section>
</template>

<script setup lang="ts">
import { Pinia } from 'pinia'
import { SearchFilters } from '../../search/schemas'
import { Party } from '../types'

const props = withDefaults(
  defineProps<{
    campsiteName?: string
    searchFilters?: SearchFilters
    pitchtypes?: Array<{ id: string; name: string }>
    dateRange?: { start: string; end: string }
    party?: Party
    isSearchResultAlert?: boolean
  }>(),
  {
    isSearchResultAlert: false,
  },
)

const userAllocationAlertsStore = useUserAllocationAlertsStore(
  usePinia() as Pinia,
)
const submitted = ref(false)
const isMounted = ref(true)
const singlePitchType = computed(() => {
  return props.pitchtypes && props.pitchtypes.length === 1
})

onUnmounted(() => {
  isMounted.value = false
})

const hideForm = async () => {
  if (!isMounted.value) return
  submitted.value = true
  await userAllocationAlertsStore.fetchAlerts()
}
</script>

<style scoped>
section {
  background: var(--colour-page-background);
  margin: 0;
  padding: 1em 0.8em 1.5em;
  position: relative;
}

h4 {
  color: #004c75;
  line-height: 1.1;
}

.expand-title {
  font-size: var(--body);
  color: white;
  line-height: 1.4;
  font-weight: normal;
}

.expand-chevron .chevron-l,
.expand-chevron .chevron-r {
  border-color: white;
  background-color: white;
}

section .ui-collapse {
  border-bottom: none;
  max-width: 30em;
}

section :deep(.ui-collapse-header) {
  background: #0072bc;
  border-radius: 0.5em 0.5em;
  margin-top: 0.5em;
  padding: 0.75em 1em;
}

section :deep(.is-active .ui-collapse-header) {
  border-radius: 0.5em 0.5em 0 0;
}

section :deep(.ui-collapse-icon.end span) {
  border-color: white;
  background: white;
}

.thanks-msg {
  padding: 1em;
  background: white;
  border-radius: 0.5em;
  font-size: var(--body);
  display: flex;
  align-items: flex-start;
  margin-top: 0.5em;
}

.tick-icon {
  width: 1.5em;
  margin: 0.25em 0.5em 0 0;
  flex-shrink: 0;
}

/* desktop & tablet */
.notify-me.desktop {
  padding: 1em var(--page-width-padding) 1.5em;
}

.notify-me.desktop :deep(.cs-availability-form:not(.multiple)) {
  max-width: 30em;
  margin: auto;
}

.notify-me.desktop :deep(.ui-collapse) {
  margin: auto;
}
</style>
